import React from "react";

/**
 * Logo system for Mon Séjour en Bretagne.
 *
 * Props:
 *   design: "monogram" | "lighthouse" | "wave" | "ermine"  (default "monogram")
 *   variant: "full" (default) | "mark" | "wordmark"
 *   theme:   "dark" (default) | "light"
 *   size:    height in px for the mark (default 40)
 */
export default function Logo({
  design = "lighthouse",
  variant = "full",
  theme = "dark",
  size = 40,
  className = "",
}) {
  const fg = theme === "light" ? "#FFFFFF" : "#0A0A0A";
  const accent = theme === "light" ? "#C9986A" : "#8B6B3D";
  const subFg = theme === "light" ? "rgba(255,255,255,0.7)" : "rgba(10,10,10,0.55)";

  const Mark = (() => {
    switch (design) {
      case "lighthouse":
        return <LighthouseMark size={size} fg={fg} accent={accent} />;
      case "wave":
        return <WaveMark size={size} fg={fg} accent={accent} />;
      case "ermine":
        return <ErmineMark size={size} fg={fg} accent={accent} />;
      case "monogram":
      default:
        return <MonogramMark size={size} fg={fg} accent={accent} />;
    }
  })();

  if (variant === "mark") return <div className={className}>{Mark}</div>;

  const Wordmark = (
    <div className="leading-none">
      <div
        className="font-display font-bold tracking-[-0.02em]"
        style={{ color: fg, fontSize: size * 0.46, lineHeight: 1 }}
      >
        Mon Séjour
      </div>
      <div
        className="font-display italic tracking-[-0.01em]"
        style={{ color: fg, fontSize: size * 0.46, lineHeight: 1.05, marginTop: size * 0.02, fontWeight: 400 }}
      >
        en Bretagne
      </div>
    </div>
  );

  if (variant === "wordmark") return <div className={className}>{Wordmark}</div>;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {Mark}
      {Wordmark}
    </div>
  );
}

/* ============================== Mark variants ============================== */

/**
 * Design 1: MONOGRAM
 * Elegant interlocked "MSB" in editorial serif inside a refined circle.
 * Aesthetic: luxury hôtel particulier, maison d'édition, yacht club.
 */
function MonogramMark({ size, fg, accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-label="Mon Séjour en Bretagne">
      <circle cx="32" cy="32" r="30" fill="none" stroke={fg} strokeWidth="1.5" />
      <circle cx="32" cy="32" r="26" fill="none" stroke={fg} strokeWidth="0.5" opacity="0.4" />
      {/* MSB monogram in serif style */}
      <g fill={fg}>
        <text
          x="32" y="40"
          textAnchor="middle"
          fontFamily="'Playfair Display', Georgia, serif"
          fontSize="22"
          fontWeight="600"
          letterSpacing="-0.5"
          fontStyle="italic"
        >M<tspan fill={accent}>·</tspan>S<tspan fill={accent}>·</tspan>B</text>
      </g>
      {/* tiny ornament: two dots at top and bottom on the outer circle */}
      <circle cx="32" cy="2" r="1.2" fill={fg} />
      <circle cx="32" cy="62" r="1.2" fill={fg} />
    </svg>
  );
}

/**
 * Design 2: LIGHTHOUSE
 * Refined Brittany lighthouse — clean geometry, narrow proportions, soft beam.
 * Inspired by Phare d'Eckmühl / Phare de la Vieille engravings.
 * Aesthetic: editorial nautical, premium hotel branding, art-print quality.
 */
function LighthouseMark({ size, fg, accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-label="Mon Séjour en Bretagne">
      {/* outer frame - thin, refined */}
      <circle cx="32" cy="32" r="30" fill="none" stroke={fg} strokeWidth="1.2" />
      <circle cx="32" cy="32" r="26.5" fill="none" stroke={fg} strokeWidth="0.4" opacity="0.35" />

      {/* soft beam of light - ocre gradient triangle */}
      <defs>
        <linearGradient id="beam-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={accent} stopOpacity="0.85" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points="34,18 58,12 58,24" fill="url(#beam-grad)" />

      {/* lighthouse silhouette */}
      <g stroke={fg} strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" fill="none">
        {/* base (rocks/terrace) - wider trapezoid */}
        <path d="M20 50 L21.5 44 L42.5 44 L44 50 Z" />
        {/* lower tower (narrower than base) */}
        <path d="M23 44 L23.5 32 L40.5 32 L41 44" />
        {/* upper tower (slightly tapered) */}
        <path d="M25 32 L25.5 24 L38.5 24 L39 32" />
        {/* gallery (small platform line) */}
        <line x1="24" y1="24" x2="40" y2="24" strokeWidth="1.6" />
        {/* light room */}
        <rect x="27" y="18" width="10" height="6" />
        {/* small roof / dome */}
        <path d="M27 18 L37 18 L35 15 L29 15 Z" />
        {/* antenna */}
        <line x1="32" y1="15" x2="32" y2="11" />
      </g>

      {/* dome tip */}
      <circle cx="32" cy="11" r="1" fill={fg} />

      {/* lamp glow inside light room */}
      <circle cx="32" cy="21" r="1.2" fill={accent} />

      {/* horizon - subtle */}
      <line x1="10" y1="50" x2="54" y2="50" stroke={fg} strokeWidth="0.7" opacity="0.5" />

      {/* sea waves */}
      <g stroke={fg} strokeWidth="0.8" fill="none" strokeLinecap="round">
        <path d="M10 54 Q15 52 20 54 T30 54 T40 54 T50 54 L54 54" opacity="0.7" />
        <path d="M10 57 Q15 55 20 57 T30 57 T40 57 T50 57 L54 57" opacity="0.35" />
      </g>
    </svg>
  );
}

/**
 * Design 3: WAVE & SUN
 * Minimal half-sun rising over a single curved horizon wave.
 * Aesthetic: editorial coastal, slow travel, contemporary minimalism.
 */
function WaveMark({ size, fg, accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-label="Mon Séjour en Bretagne">
      <rect x="1" y="1" width="62" height="62" fill="none" stroke={fg} strokeWidth="1" />
      {/* sun half-circle */}
      <path d="M14 34 A18 18 0 0 1 50 34" fill={accent} stroke="none" />
      {/* sun rays - thin diagonal */}
      <g stroke={fg} strokeWidth="0.8" strokeLinecap="round">
        <line x1="32" y1="8" x2="32" y2="14" />
        <line x1="14" y1="14" x2="18" y2="18" />
        <line x1="50" y1="14" x2="46" y2="18" />
      </g>
      {/* horizon line */}
      <line x1="6" y1="34" x2="58" y2="34" stroke={fg} strokeWidth="1.2" />
      {/* wave curves */}
      <g stroke={fg} strokeWidth="1.4" fill="none" strokeLinecap="round">
        <path d="M6 44 Q14 40 22 44 T38 44 T54 44 L58 44" />
        <path d="M6 52 Q14 48 22 52 T38 52 T54 52 L58 52" opacity="0.45" />
      </g>
    </svg>
  );
}

/**
 * Design 4: ERMINE
 * Single refined "moucheture d'hermine" symbol - the most Breton mark.
 * Aesthetic: heraldic, signature stamp, refined regional identity.
 */
function ErmineMark({ size, fg, accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-label="Mon Séjour en Bretagne">
      {/* shield-style outer frame */}
      <path d="M6 6 L58 6 L58 42 Q58 56 32 62 Q6 56 6 42 Z" fill="none" stroke={fg} strokeWidth="1.5" />
      {/* inner thin frame */}
      <path d="M10 10 L54 10 L54 41 Q54 53 32 58 Q10 53 10 41 Z" fill="none" stroke={fg} strokeWidth="0.5" opacity="0.4" />

      {/* The ermine spot (moucheture d'hermine), centered and large */}
      <g fill={fg}>
        {/* The classic moucheture: 3 small dots above a black tear/arrowhead with 3 points */}
        <circle cx="26" cy="22" r="1.3" />
        <circle cx="32" cy="20" r="1.3" />
        <circle cx="38" cy="22" r="1.3" />
        {/* Main body - arrowhead/tear shape */}
        <path d="M32 24
                 C28 24, 25 28, 25 33
                 C25 37, 27 40, 28 42
                 L26 46
                 L29 45
                 L30 48
                 L32 45
                 L34 48
                 L35 45
                 L38 46
                 L36 42
                 C37 40, 39 37, 39 33
                 C39 28, 36 24, 32 24 Z" />
      </g>

      {/* tiny accent dots in corners */}
      <circle cx="14" cy="14" r="0.8" fill={accent} />
      <circle cx="50" cy="14" r="0.8" fill={accent} />
    </svg>
  );
}
